import { useCallback, useEffect, useRef, useState } from "react";
import { devPreviewEmailForRequests } from "../../../utils/userAccess";

/**
 * Load data for one card. `key` should change whenever the inputs change
 * (e.g. JSON of the filters); stale responses are ignored.
 */
export function useWidgetData<T>(key: string, load: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  // Switching the dev "view as" manager must refetch even though filters are unchanged.
  const previewEmail = devPreviewEmailForRequests() ?? "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(`Home card load failed (${key}):`, e);
        setError(e?.response?.data?.error || e?.message || "Could not load this summary");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, previewEmail, reloadToken]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);
  return { data, loading, error, reload };
}
